import React, { useState, useEffect } from 'react';
import { Calendar, AlertTriangle, CheckCircle, RefreshCw, Layers } from 'lucide-react';
import { auditService } from '../auditService';

export const AuditPlanMatrixTab: React.FC = () => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [planData, setPlanData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    loadPlanMatrix();
  }, [year]);

  const loadPlanMatrix = async () => {
    setLoading(true);
    try {
      const data = await auditService.getPlanView(year);
      setPlanData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load audit plan matrix:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Banner */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Calendar size={18} className="text-indigo-600" />
            Annual Quality Audit Calendar & Red Alarm Matrix (Plan View)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            EASA Part-CAMO/145/ORO 12-month regulatory surveillance interval tracking and overdue alarms
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600">Surveillance Year:</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 font-semibold focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
          </select>
          <button
            onClick={loadPlanMatrix}
            className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600"
            title="Refresh Plan"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-center text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase font-bold">
            <tr>
              <th className="py-3 px-4 text-left">Department / Audit Scope</th>
              <th className="py-3 px-2">Cycle</th>
              <th className="py-3 px-2">Red Alarm Status</th>
              {months.map((m) => (
                <th key={m} className="py-3 px-2">{m}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={15} className="py-8 text-slate-400">Loading annual audit plan matrix...</td>
              </tr>
            ) : planData.length === 0 ? (
              <tr>
                <td colSpan={15} className="py-8 text-slate-400">No annual audit periods configured for {year}.</td>
              </tr>
            ) : (
              planData.map((row, idx) => {
                const isAlarm = Boolean(row.is_red_alarm);
                return (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="py-3 px-4 text-left font-semibold text-slate-800">
                      {row.department_name || `Department ${row.department_id || idx + 1}`}
                      <span className="block text-[10px] text-slate-400 font-normal">{row.audit_area || 'Quality Assurance'}</span>
                    </td>
                    <td className="py-3 px-2 font-mono text-slate-600">
                      {row.frequency_months ? `${row.frequency_months}m` : '12m'}
                    </td>
                    <td className="py-3 px-2">
                      {isAlarm ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 animate-pulse">
                          <AlertTriangle size={12} /> RED ALARM
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                          <CheckCircle size={12} /> OK
                        </span>
                      )}
                    </td>

                    {/* 12 Months Indicators */}
                    {months.map((_, mIdx) => {
                      const planned = row.scheduled_months?.includes(mIdx + 1) || (row.target_month === mIdx + 1);
                      return (
                        <td key={mIdx} className="py-3 px-2">
                          {planned ? (
                            <span className="inline-block w-4 h-4 rounded-full bg-indigo-600 text-white font-bold text-[9px] leading-4" title="Audit Planned">
                              ●
                            </span>
                          ) : (
                            <span className="text-slate-200">·</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
